import React, { useEffect, useState, useCallback } from 'react';
import { useWalkthrough } from './WalkthroughProvider';
import WalkthroughTooltip from './WalkthroughTooltip';

const PADDING = 8;

const WalkthroughOverlay: React.FC = () => {
  const { isActive, currentStep, skipWalkthrough } = useWalkthrough();
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  const findTarget = useCallback(() => {
    if (!currentStep) { setTargetRect(null); return; }
    const el = document.querySelector(currentStep.element);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Small delay to let scroll settle
      setTimeout(() => {
        const rect = el.getBoundingClientRect();
        setTargetRect(rect);
      }, 300);
    } else {
      setTargetRect(null);
    }
  }, [currentStep]);

  useEffect(() => {
    if (isActive && currentStep) {
      findTarget();
    } else {
      setTargetRect(null);
    }
  }, [isActive, currentStep, findTarget]);

  // Update position on resize/scroll
  useEffect(() => {
    if (!isActive) return;
    const handler = () => findTarget();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [isActive, findTarget]);

  if (!isActive) return null;

  // Spotlight cutout dimensions
  const spotX = targetRect ? targetRect.left - PADDING : 0;
  const spotY = targetRect ? targetRect.top - PADDING : 0;
  const spotW = targetRect ? targetRect.width + PADDING * 2 : 0;
  const spotH = targetRect ? targetRect.height + PADDING * 2 : 0;

  return (
    <>
      {/* Overlay with cutout */}
      <svg
        onClick={skipWalkthrough}
        style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9000, cursor: 'pointer' }}
      >
        <defs>
          <mask id="walkthrough-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect x={spotX} y={spotY} width={spotW} height={spotH} rx="8" ry="8" fill="black" />
            )}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.5)" mask="url(#walkthrough-mask)" />
      </svg>

      {/* Spotlight ring */}
      {targetRect && (
        <div style={{
          position: 'fixed',
          top: spotY,
          left: spotX,
          width: spotW,
          height: spotH,
          borderRadius: '8px',
          boxShadow: '0 0 0 4px rgba(59,130,246,0.5)',
          zIndex: 9001,
          pointerEvents: 'none',
        }} />
      )}

      {/* Tooltip */}
      {targetRect && <WalkthroughTooltip targetRect={targetRect} />}

      {/* Fallback when element not found */}
      {!targetRect && currentStep && (
        <div style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          backgroundColor: 'white', padding: '32px', borderRadius: '12px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)', zIndex: 9002, textAlign: 'center', maxWidth: '400px',
        }}>
          <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
            {currentStep.title}
          </h4>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
            {currentStep.description}
          </p>
          <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px' }}>
            Navigate to the relevant page to see the highlighted element.
          </p>
          <button
            onClick={skipWalkthrough}
            style={{
              padding: '8px 20px', backgroundColor: '#3b82f6', color: 'white',
              border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      )}
    </>
  );
};

export default WalkthroughOverlay;
